import { CollectionData } from "data/collections";

export enum ProductStatus {
  DRAFT = "Draft",
  PUBLISHED = "Published",
  ARCHIVED = "Archived",
}

export enum ExampleMediaType {
  AUDIO = "audio",
  IMAGE = "image",
  VIDEO = "video",
  SPOTIFY = "spotify",
  YOUTUBE = "youtube",
  TIKTOK = "tiktok",
}

export interface ExampleMedia {
  mediaId: string;
  mediaLink?: string;
  mediaType?: ExampleMediaType;
  mediaTitle?: string;
  mediaThumbnailImage?: string;
}

// export interface PurchaseableItem {
//   id: string;
//   title: string;
//   priceInfo: PriceData;
// }

export type PurchaseableItem = ProductData | CollectionData;

export interface ProductData extends CollectionData {
  id: string;
  storefrontId: string;
  status: ProductStatus;
  title: string;
  description: string;
  dateDescription?: string;
  productImageURL: string;
  productPreviewURL: string;
  createdAt: string;
  lastUpdatedAt: string;
  creatorName: string;
  creatorProfileImageURL: string;
  storefrontSlug: string;
  totalVideoLengthInSeconds: number;
  creatorBio: string;
  shortBio?: string;
  creatorOnStudioSinceDate: string;
  categories?: string[];
  tags: string[];
  genreTags: string[];
  content: ContentData[];
  whoIsThisForSection: string[];
  skills: string[];
  exampleMediaSectionTitle: string;
  exampleMediaSectionDescription: string;
  exampleMedia?: ExampleMedia[];
  overviewSectionTitle: string;
  overviewSectionDescription: string;
  classSlug: string;
  previewClipURL?: string;
  priceInfo: PriceData;
  ratingInfo?: RatingData;
  orderIndex: number;
  recommendationPassthroughId?: string;
  classLearnOutcome?: string;
  legacyClass?: string;
  freeLessonDescription?: string;
  isNotAvailableForPurchase?: boolean;
  isMoreAvailable?: boolean;
  objectID?: string;
  isCardPreviewNotClickable?: boolean;
  cardPreviewDescriptionOverride?: string;
  checkoutSubtitle?: string;
  checkoutTitleAddition?: string;
  checkoutCustomTerms?: string;
  isSchool?: boolean;
  subscriptionSlug?: string;

  // for music library
  currentContentId?: string;
  currentContentProgressPercentage?: number;
}

export interface PriceData {
  price: number;
  crossedPrice: number;
  crossedPriceApplicableFrom?: string;
  applicableFrom?: string;
  shouldNotHideDiscount?: boolean;
  specialOnlyVip?: boolean;
}

export interface RatingData {
  ratingScore: number;
  numRatings: number;
  numTakers: number;
}

export interface ContentData {
  id: string;
  title: string;
  mediaUrl: string;
  previewImageUrl: string;
  thumbnail: string;
  lengthInSeconds: number;
  description: string;
  contentNotes: string;
  fileName: string;
}

export interface UserProductPurchaseData {
  id: string;
  productId: string;
  userId: string;
  storefrontId: string;
  createdAt: string;
  status: string;
  productInfo: ProductData;
  contentProgress?: Record<string, ContentProgressData>;
  completionPercentage?: number;
  totalSecondsWatched?: number;
  lastPositionPercentage?: number;
  latestEngagedContent?: string;
  latestEngagedContentId?: string;
  latestContentPosition?: number;
  lastEngagedWithAt?: string;
  refundedInfo?: RefundedInfo;
}

export interface RefundedInfo {
  reasonKey: string;
  reasonText: string;
  refundDate: string;
  refundId: string;
}

export interface ContentProgressData {
  completionPercentage: number;
  totalSecondsWatched: number;
  lastPositionInSeconds: number;
  lastPositionPercentage: number;
  lastEngagedWithAt: string;
}

export interface ProductProgressData {
  productId: string;
  lastPositionPercentage: number;
  latestEngagedContentId: string;
  latestContentPositionInSeconds: number;
  completionPercentage: number;
  totalSecondsWatched: number;
  lastEngagedWithAt: string;
}

// export const freqDummyData: ProductData[] = [
//   {
//     content: [
//       {
//         contentNotes:
//           "\nFor more information on the history of what was covered in this video, you can check out the following references:\n\n- The oldest known reference in print for the key card principle is Girolamo Cardano’s _De subtilitate_, 1550 (early in Book XV).\n- The “next card I flip over” bet is the so-called “Circus Card Trick”. In the classic text _Expert Card Technique_ from 1940 it is already called an “old, old trick”, its roots being unknown.\n- The muscle reading reveal is based on the “Hellstrom Card Trick” from Oscar Hugo’s book _Magi Card System_, 1942.\n- Revealing a selection by spelling its name was most likely invented by Dr. James Elliott. Supporting research was published in Gibeciere, Vol. 5 No. 1, Winter 2010.\n- Pit Hartling’s “Finger Flicker” was published in his book _Card Fictions_, 2003. The technique behind this trick comes from the gambling community and while circulating through the underground for a while was finally introduced to a larger audience in the book _The Vernon Chronicles Volume 1_ by Stephen Minch, 1987.\n",
//         id: "0a8e4b71-b35f-4acd-b9ca-257799724649",
//         lengthInSeconds: 1402,
//         mediaUrl: "https://monthly.wistia.com/medias/aphvfe0iy5",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/2f9c75db00e9f2568b118a38853f05e9.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "The Key Card Principle",
//       },
//     ],
//     createdAt: "2022-11-07T20:51:43Z",
//     creatorBio: "",
//     creatorName: "David Blaine",
//     creatorOnStudioSinceDate: "2022-10-01T20:51:43Z",
//     creatorProfileImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675192599/monthly-admin-images/behind-scenes-product/product-images/david-blaine-welcomeposter.jpg",
//     description:
//       "In this video, I’m going to teach you my favorite magic principle. The principle is simple (you’ll be able to perform it right away), but also incredibly powerful (this is the principle I use when trying to fool other magicians).\n\n I’ll teach you 5 powerful card tricks using this principle, and I’ll also show you how to invent your own tricks.\n\n If you’ve ever been curious to learn magic or want to see my approach, this is a great place to start. I’ve never shared my secrets anywhere else. In 20 minutes, I’ll bring you fully into my world and you’ll leave ready to perform mind-blowing illusions.",
//     id: "a9fad403-f088-4976-a43c-e5557822092b",
//     orderIndex: 0,
//     priceInfo: {
//       crossedPrice: 4999,
//       crossedPriceApplicableFrom: "2023-03-10T07:59:59Z",
//       price: 1499,
//     },
//     productImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675281483/monthly-admin-images/behind-scenes-product/product-images/david-blaine-product-1.jpg",
//     productPreviewURL: "https://monthly.wistia.com/medias/3oq2zexkfh",
//     ratingInfo: { numRatings: 305, numTakers: 5385, ratingScore: 4.94 },
//     storefrontSlug: "david-blaine",
//     tags: [
//       "Magic",
//       "Card Magic",
//       "Performance",
//       "Psychology",
//       "Close-up Magic",
//       "Mentalism",
//       "Sleight of Hand",
//       "Card Tricks",
//       "Illusions",
//       "Cards",
//       "Audience",
//     ],
//     title: "Card Trick #1: Learn My Favorite Magic Principle In 20 Minutes",
//     totalVideoLengthInSeconds: 1402,
//     whoIsThisForSection: [
//       "You’re a **fan of David Blaine** and are **curious to see what happens behind the scenes.** You’re looking for inspiration and access to a world you normally can’t see.",
//       "You want to **learn exactly how David Blaine does what he does.** Watch his **real, start-to-finish process**, including how he navigates every decision, setback, and more.",
//       "You want to **know all of David Blaine’s insider secrets** — the exact tools and techniques he uses, his hard-won lessons, and well-guarded industry secrets.",
//     ],
//   },
//   {
//     content: [
//       {
//         contentNotes: "Mark explains how the best build ideas are motivated by great stories.",
//         id: "b79d878d-c1c8-4a8d-8314-128de0e98dbc",
//         lengthInSeconds: 620,
//         mediaUrl: "https://monthly.wistia.com/medias/gmgo9zggwh",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/5ae730555c79b99571dea3ee36e0a385.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Story-Motivated Builds",
//       },
//       {
//         contentNotes: "Mark narrows in on an idea for his build, using his brainstorming process.",
//         id: "a541dbc6-2f13-4a32-b77b-ebd9ee382933",
//         lengthInSeconds: 2327,
//         mediaUrl: "https://monthly.wistia.com/medias/i7nojeb93f",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/6d048568db9f51a42803be511a87fcc5.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Brainstorming",
//       },
//       {
//         contentNotes:
//           "Mark shows how to fully and properly research your ideas and define requirements for your builds.",
//         id: "94382eb6-e94b-4a87-8817-cdce798cf9bb",
//         lengthInSeconds: 3059,
//         mediaUrl: "https://monthly.wistia.com/medias/x0e9ging7v",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/6ff2dbc7e1c8b4907053dfcf5fc6105e93095197.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Research Part 1: Requirements & Exploration",
//       },
//       {
//         contentNotes:
//           "Mark demonstrates how to evaluate various design options and settle on a direction for your build.",
//         id: "9c831a57-09f0-4d02-8507-eb0d4b39eb65",
//         lengthInSeconds: 2532,
//         mediaUrl: "https://monthly.wistia.com/medias/0oditvjdwm",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/0dfafa648440446f1518d24c1b194c11ff1b01a4.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Research Part 2: Experimentation & Design",
//       },
//       {
//         contentNotes:
//           "Mark demonstrates how to construct a fast and functional prototype for a build that can continue to be iterated on. ",
//         id: "9b4eba56-d0d3-42b7-88ef-320c1c2124a8",
//         lengthInSeconds: 5816,
//         mediaUrl: "https://monthly.wistia.com/medias/gf32wnxnvc",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/f864ce7774e4633a580eb002c0c64788.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Constructing a Functional Prototype",
//       },
//       {
//         contentNotes:
//           "Mark shows how to take a prototype over the finish line, making it functional, aesthetic, and able to tell a story. ",
//         id: "77f654ee-bf80-412d-ab42-69337ee10e47",
//         lengthInSeconds: 2526,
//         mediaUrl: "https://monthly.wistia.com/medias/lg3b4bvoaq",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/01baee5c06042d16795c0889217801cf.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Putting the Pieces Together & Finalizing the Build",
//       },
//     ],
//     createdAt: "2021-01-11T20:51:43Z",
//     creatorBio: "",
//     creatorName: "Mark Rober",
//     creatorOnStudioSinceDate: "2021-10-01T20:51:43Z",
//     creatorProfileImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675191530/monthly-admin-images/behind-scenes-product/product-images/mark-rober-welcomeposter.jpg",
//     description:
//       "In this class, I share my entire, start-to-finish engineering process on camera, as I build a brand new invention from scratch, right in front of you. Along the way, I share and explain everything that I'm doing and thinking in detail — every idea, decision, tool, and technique.\n\n If you've ever wondered how to bring your ideas and imagination to life, you can follow the step-by-step engineering design process I teach in this series to do exactly that. It's the same process I use for all my YouTube videos, and the process I used at NASA and Apple.\n\n Essentially, you'll learn exactly how I do what I do behind the scenes, from how I come up with my viral build ideas all the way through bringing the final builds to life.\n\n In the process, I'll be explaining my approach to the following topics:\n - The Engineering Design Process\n - Creativity and Coming Up with Ideas\n - Materials and Build Techniques\n - Principles of Mechanical Engineering\n - Pneumatics, Springs, Motors, Solenoids, and Advanced Tools\n- Research and Exploring Design Options\n - Prototyping and Testing Designs\n - Designing and Running Experiments\n - Telling the Story of Your Build\n - And a lot more\n\n In this series, I also make sure to teach you all of the skills you need to successfully follow along with my process. While you watch, feel free to follow along step-by-step to create your own creative invention alongside me.",
//     id: "cb4d8d03-ca12-45d3-b841-328c1038526e",
//     orderIndex: 0,
//     priceInfo: {
//       crossedPrice: 12999,
//       crossedPriceApplicableFrom: "2023-03-10T07:59:59Z",
//       price: 4999,
//     },
//     productImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675281484/monthly-admin-images/behind-scenes-product/product-images/mark-rober-product-1.jpg",
//     productPreviewURL: "https://monthly.wistia.com/medias/z7g03aokpg",
//     ratingInfo: { numRatings: 1683, numTakers: 48275, ratingScore: 4.73 },
//     storefrontSlug: "mark-rober",
//     tags: [
//       "Engineering",
//       "Creative Engineering",
//       "Inventing",
//       "Creativity",
//       "Design Process",
//       "Brainstorming",
//       "Research",
//       "Experimentation",
//       "Prototyping",
//       "Mechanical Engineering",
//       "Woodworking",
//       "Creative Ideas",
//       "Tools",
//       "Maker",
//       "DIY",
//       "Building",
//     ],
//     title: "Building a New Invention From Start to Finish: My Full Creative Engineering Process",
//     totalVideoLengthInSeconds: 16880,
//     whoIsThisForSection: [
//       "You’re a **fan of Mark Rober** and are **curious to see what happens behind the scenes.** You’re looking for inspiration and access to a world you normally can’t see.",
//       "You want to **learn exactly how Mark Rober does what he does.** Watch his **real, start-to-finish process**, including how he navigates every decision, setback, and more.",
//       "You want to **know all of Mark Rober’s insider secrets** — the exact tools and techniques he uses, his hard-won lessons, and well-guarded industry secrets.",
//     ],
//   },
//   {
//     content: [
//       {
//         contentNotes:
//           "\nIn this video, you will follow along with Christina step-by-step as she bakes her Cornflake Chocolate Chip Marshmallow Cookie. She’ll walk you through the science of cookies, and all of the baking techniques she uses to get the best cookie results every time.\n\nIf you want to bake the cookies with Christina, we recommend watching this video in the kitchen and following everything she does step-by-step in real-time. If you’d rather watch the video in its entirety before baking your own cookies, that's okay too, it will just take a bit more time.\n\nThe recipe makes 15-20 cookies. You can print out the [ingredients and recipe](https://res.cloudinary.com/monthly/image/upload/v1649714166/monthly-admin-images/christina-tosi-baking/studio-tosi-project-cornflake-cookie.pdf) if you want to reference it while you watch. The recipe and ingredients you need are also listed just below.\n\n### What you need\n\n**Ingredients for the Cornflake Crunch:**\n\n- 130g cornflakes\n- 30g milk powder (⅓ cup + 1 Tbsp)\n- 30g sugar (2 Tbsp + 1 tsp)\n- 3g kosher salt (¾ tsp)\n- 100g butter\n\n**Ingredients for the Cornflake Chocolate Chip Marshmallow Cookie:**\n\n- 225g unsalted butter, at room temperature (2 sticks)\n- 250g granulated sugar (1 cup)\n- 150g light brown sugar (3/4 cup tightly packed)\n- 1 egg\n- 2g vanilla extract (1⁄2 tsp)\n- 260g flour\n- 2g baking powder (1⁄2 tsp)\n- 1.5g baking soda (1⁄2 tsp)\n- 5g kosher salt (1 1⁄2 tsp)\n- 125g mini chocolate chips\n- 65g mini marshmallows\n\n**Materials:**\n\n- Stand mixer\n- Kitchen scale\n- Small and medium bowls and containers\n- Spatula\n- 2 ¾ oz cookie scoop or 1⁄3 cup measure\n- Parchment paper\n- Sheet pan\n\n### What to do\n\n**Make the Cornflake Crunch (30 minutes)**\n\n1. Heat the oven to 275° F (135° C).\n - If you’re using an oven thermometer, check to make sure that your oven is at the correct temperature.\n2. Pour the cornflakes in a medium bowl and crush them with your hands to a half of their original size.\n3. Add the milk powder, sugar, and salt and toss to mix.\n4. Melt your butter into liquid form, using either a microwave or a stove.\n5. Add the butter and toss to coat until everything seems evenly coated.\n6. Using your fingers or a spatula, spread the mixture evenly on a parchment paper lined sheet pan.\n7. Bake for 20 minutes, at which point your cornflakes and milk powder should look golden brown, smell buttery, and crunch gently when chewed after they cool.\n8. While you wait, clean up your workstation. If you’re planning on making the cookies immediately after the crunch, you can also prepare all of your ingredients and equipment for the cookies.\n - Make sure to take your two sticks of butter out of the fridge so that they reach room temperature.\n9. Set your Cornflake Crunch aside to cool. While it cools, you can start preparing your cookie dough. Or, if you are planning on making your cookies later, let the crunch cool and then store it.\n - Stored in an airtight container at room temperature, the crunch will keep fresh for 1 week; in the fridge or freezer it will keep for 1 month.\n - If you don’t have time to make your cookies now you can pause here, store your Cornflake Crunch, and continue on later.\n\n_Checkpoint 1: You can pause here if you’d rather continue later today or tomorrow._\n\n**Make your cookie dough (15 minutes)**\n\n1. Preheat the oven to 375° F (190° C), and check the temperature with your oven thermometer if using.\n2. If your butter is still colder than room temperature, you can microwave it in increments of no more than 5 seconds. Do not melt your butter in the microwave - it is better to use slightly colder butter than melted butter in this recipe.\n3. Combine the butter and sugars in the bowl of a stand mixer fitted with the paddle attachment and cream together on medium-high speed for 2 to 3 minutes.\n4. Scrape down the sides of the bowl and the paddle, add the egg and vanilla, and beat on high for 7 to 8 minutes.\n - Check on your mixture every few minutes and scrape down the paddle and sides of the bowl as needed.\n - When you are done, scrape down the paddle and sides one final time.\n5. Measure your flour, baking powder, baking soda, and salt together and mix them with a spoon in a separate container.\n6. Add your dry ingredients and set mixer speed to low. Mix just until the ingredients are evenly distributed, or for about 15-45 seconds. Scrape down the sides of the bowl with a spatula.\n - It’s ok if your dough is a bit shaggy at this point! Your dough will mix for more time after you add in your mix-ins.\n7. Add your Cornflake Crunch, mini chocolate chips, and mini marshmallows.\n8. On low speed, mix just until mix-ins are incorporated, no more than 30 to 45 seconds.\n\n**Make and assess a tester cookie (15 minutes)**\n\n1. Using a 2 ¾ oz ice cream scoop (or a 1⁄3 cup measure), scoop one portion of your dough onto a parchment-lined sheet pan.\n2. Put the cookie in the oven and set a timer for 12 minutes.\n3. While you wait, you should start the instructions below to scoop and refrigerate the rest of your cookies.\n4. At the 12 minute mark, take out your cookie and look at it. The cookie should be browned on the edges and just beginning to brown toward the center. You can refer to Christina’s cookie to know how yours should look.\n - If your cookie is too light, leave it in the oven for a minute at a time until it is the color you want.\n - If your cookie is too dark, make sure to bake your full batch of cookies for less than 12 minutes next time.\n\n**Scoop and refrigerate your dough (5 minutes + 1 hour of fridge time)**\n\n1. Scoop your cookie dough onto parchment-lined sheet pans using your 2 ¾ oz ice cream scoop (or a 1⁄3 cup measure) a minimum of 3 inches apart.\n2. Pat the tops of the cookie dough domes flat with your hand.\n3. Wrap the sheet pan tightly in plastic wrap and refrigerate for at least 1 hour, or up to 1 week. If you bake your cookies from room temperature, they will spread more and bake flatter than intended.\n\n_Checkpoint 2: You can pause here if you’d rather continue later today or tomorrow._\n\n**Bake your full batch of cookies (15 minutes)**\n\n1. Be sure to preheat the oven to your ideal temperature. This should be around 375° F (190° C).\n2. Bake for your ideal time, around 12-15 minutes, as determined by the tester assessment.\n - Be sure to account for any hot spots in your oven by rotating the pan halfway through baking.\n - Remember, it’s always safer to bake for less time because you can always put the cookies back in the oven, but you can’t undo an over-baked cookie.\n",
//         id: "f5b400b5-515d-4a9f-b289-d4e92720ba77",
//         lengthInSeconds: 7466,
//         mediaUrl: "https://monthly.wistia.com/medias/fw225g6hl1",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/e6f9b9087429480c9e9f4a284f7be134.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Foundations of Cookie: Cornflake Chocolate Chip Marshmallow Cookie",
//       },
//       {
//         contentNotes:
//           'Christina teaches how to come up with your own personal, original recipes, by developing a "flavor story" unique to your experiences and taste.',
//         id: "25110f8e-93fc-4f1a-9996-1c1822c9b39e",
//         lengthInSeconds: 2956,
//         mediaUrl: "https://monthly.wistia.com/medias/rjrgzpijrl",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/7aa42440b510dce4cf19b2b80649ad084d406e66.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Starting R&D: Finding a Flavor Story",
//       },
//       {
//         contentNotes:
//           "Christina explains how to incorporate your flavor story into your cookie and plan prototypes.",
//         id: "66540bcb-2b51-41d1-9b34-0c9fa8b0293b",
//         lengthInSeconds: 2174,
//         mediaUrl: "https://monthly.wistia.com/medias/z8thcqm51o",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/3016ff2a76a37432aeb0f3fc60abe184.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Planning the Prototypes",
//       },
//       {
//         contentNotes:
//           "Christina talks through her ingredient selection process, shopping in her pantry for items that might tell her flavor story, and considering how.",
//         id: "db60fcf3-2471-46a4-beea-3c56ccef3a6b",
//         lengthInSeconds: 1280,
//         mediaUrl: "https://monthly.wistia.com/medias/dfhng384y1",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/e89f6eb917f86aa625ef30b5fe77b080467849d8.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Selecting Ingredients",
//       },
//       {
//         contentNotes:
//           "Christina teaches how to bring a flavor story to life by constructing and assessing prototypes.",
//         id: "e6d3ff24-9928-4dd4-a14d-2c75ab540606",
//         lengthInSeconds: 2674,
//         mediaUrl: "https://monthly.wistia.com/medias/2vmjonmswx",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/eeef5ced3b19aee79cd6f52aaa7eb16dd1b630ff.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Prototype Prep & Making a Custom Dough Base",
//       },
//       {
//         contentNotes:
//           "Christina starts creating her new cookie by assembling several prototype cookies made of ingredients that fit her flavor story.",
//         id: "1483206b-b685-403a-9832-df30cedb6809",
//         lengthInSeconds: 2499,
//         mediaUrl: "https://monthly.wistia.com/medias/os9rh5x43w",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/f4e9a29ffb5b3f304a227e175f6141ad35da14bb.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Creating Cookie Prototypes",
//       },
//       {
//         contentNotes:
//           "Christina improves her new recipe even more using her assessing and iterating process to taste and improve each element.",
//         id: "e8bd41cf-1249-452d-aa88-2cf3b078f0bc",
//         lengthInSeconds: 2014,
//         mediaUrl: "https://monthly.wistia.com/medias/xhm4t58quh",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/8ff8f0401fe65c14ec9205e5ec4191fb.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Assessing and Iterating",
//       },
//       {
//         contentNotes:
//           "Christina teaches how to take the original cookie recipe to the next level by refining and improving the interplay of mix-ins.",
//         id: "fd4f3437-2e4b-4035-8f33-b2e598afaf87",
//         lengthInSeconds: 2409,
//         mediaUrl: "https://monthly.wistia.com/medias/1a56w3zheq",
//         previewImageUrl:
//           "https://embed-ssl.wistia.com/deliveries/00050b07c2579dbe4e9b240ec8d2e134f77f1c65.jpg?image_crop_resized=640x360",
//         description: "",
//         title: "Refining and Writing the Recipe",
//       },
//     ],
//     createdAt: "2021-01-11T20:51:43Z",
//     creatorBio: "",
//     creatorName: "Christina Tosi",
//     creatorOnStudioSinceDate: "2021-10-01T20:51:43Z",
//     creatorProfileImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675192189/monthly-admin-images/behind-scenes-product/product-images/christina-tosi-welcomeposter.jpg",
//     description:
//       "In this class, I share my entire, start-to-finish creative process on camera, as I develop a brand new cookie recipe from scratch. Along the way, I share and explain everything that I'm doing and thinking in detail — every idea, decision, tool, and technique.\n\nYou'll learn the exact recipe R&D process that I use at Milk Bar, from how I come up with my initial ideas all the way through tasting the final products.\n\n In the process, I'll be explaining my approach to the following topics:\n - Baking Fundamentals\n - Controlling Flavor, Texture, and Shape\n - Building an Intuitive Understanding of Ingredients\n - The Science of Cookies\n - Precise Measuring, Creaming, and Baking\n - Gluten Formation, Leaveners, Aeration\n - Assembly and Final Preparation\n - Custom Doughs and Add-Ins\n - Substitutions and Improvisation\n - Advanced Techniques and Principles\n - Prototyping, Creativity, and Iteration\n - Recipe Inspiration and Flavor Story\n - How To Get The Most Out of Your Home Kitchen\n - And a lot more\n\n In this series, I also make sure to teach you all of the skills you need to successfully follow along with my process. While you watch, feel free to follow along step-by-step to create your own original cookie recipe alongside me.",
//     id: "4cd812bd-e17b-4a3d-b8a5-fe66ba28aa71",
//     orderIndex: 0,
//     priceInfo: {
//       crossedPrice: 14999,
//       crossedPriceApplicableFrom: "2023-03-10T07:59:59Z",
//       price: 4999,
//     },
//     productImageURL:
//       "https://res.cloudinary.com/monthly/image/upload/v1675281482/monthly-admin-images/behind-scenes-product/product-images/christina-tosi-product-1.jpg",
//     productPreviewURL: "https://monthly.wistia.com/medias/iee6y418mg",
//     ratingInfo: { numRatings: 844, numTakers: 13991, ratingScore: 4.94 },
//     storefrontSlug: "christina-tosi",
//     tags: [
//       "Baking",
//       "Creative Baking",
//       "Recipe Design",
//       "Baking Fundamentals",
//       "Cookie",
//       "Baking Science",
//       "Creativity",
//       "Prototyping",
//       "Ingredients",
//       "Doughs",
//       "Recipes",
//       "Desserts",
//       "Milk Bar",
//       "Culinary Arts",
//       "Sweets",
//     ],
//     title: "Developing an Original Cookie Recipe From Start to Finish: My Complete Process",
//     totalVideoLengthInSeconds: 23472,
//     whoIsThisForSection: [
//       "You’re a **fan of Christina Tosi** and are **curious to see what happens behind the scenes.** You’re looking for inspiration and access to a world you normally can’t see.",
//       "You want to **learn exactly how Christina Tosi does what she does.** Watch her **real, start-to-finish process**, including how she navigates every decision, setback, and more.",
//       "You want to **know all of Christina Tosi’s insider secrets** — the exact tools and techniques she uses, her hard-won lessons, and well-guarded industry secrets.",
//     ],
//   },
// ];
